import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from '../context/DarkModeContext';
import NotificationBell from './NotificationBell';

function DarkToggle() {
  const { dark, toggle } = useDarkMode();
  return (
    <button
      onClick={toggle}
      title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="p-1.5 rounded-lg text-blue-200 hover:text-white hover:bg-blue-600 transition-colors"
    >
      {dark ? (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
}

// Shared page header used by all pages except Search (which has its own
// auth-aware header with mobile filter toggle and dynamic nav).
//
// Props:
//   subtitle  — small text shown below the brand (e.g. "Admin Dashboard")
//   back      — URL for a "← Back" link shown on the right (default: "/")
//   backLabel — label for the back link (default: "← Back to search")
//   right     — arbitrary React node to render on the right instead of a back link
//   noBrand   — hide the clickable brand link (rarely needed)

export default function Header({
  subtitle,
  back = '/',
  backLabel = '← Back to search',
  right,
  noBrand = false,
}) {
  const { isAuthenticated, isAdmin, isOrgAccount, user, logout } = useAuth();
  const isVerifiedOrg = isOrgAccount && !!user?.facility_id;
  const navigate = useNavigate();

  const handleSignOut = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="bg-blue-700 text-white px-6 py-4 shadow">
      <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
        {/* Brand */}
        <div className="min-w-0">
          {noBrand ? (
            <span className="text-white font-bold text-lg tracking-tight">
              WA BHA Treatment Finder
            </span>
          ) : (
            <Link
              to="/"
              className="text-white font-bold text-lg tracking-tight hover:text-blue-100 transition-colors"
            >
              WA BHA Treatment Finder
            </Link>
          )}
          {subtitle && (
            <p className="text-blue-200 text-xs mt-0.5">{subtitle}</p>
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <DarkToggle />
          {isVerifiedOrg && (
            <Link
              to="/inbox"
              title="Secure Inbox"
              className="p-1.5 rounded-lg text-blue-200 hover:text-white hover:bg-blue-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </Link>
          )}
          {isAuthenticated && <NotificationBell />}
          {isAdmin && (
            <Link
              to="/admin"
              className="text-blue-200 hover:text-white text-sm transition-colors font-medium"
            >
              Admin
            </Link>
          )}
          {right !== undefined ? (
            right
          ) : (
            <Link
              to={back}
              className="text-blue-200 hover:text-white text-sm underline underline-offset-2 transition-colors"
            >
              {backLabel}
            </Link>
          )}
          {isAuthenticated && (
            <button
              onClick={handleSignOut}
              className="text-blue-200 hover:text-white text-sm transition-colors"
            >
              Sign out
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
